var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/*
SPDX-FileCopyrightText: 2023 Genome Research Ltd.

SPDX-License-Identifier: MIT
*/
import { useState } from "react";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import { Bar } from "react-chartjs-2";
import { Button } from '../index';
import { getChartColour, initialiseDatasets, updateChartColours, setClickedColourToSolid, setBarClickedData, generateBarLabels, updateOpacitys, resetItemClickedData } from "./ChartUtils";
import { isPropDefined, getCssVarValue } from "../general/Utils";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/free-solid-svg-icons';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
function BarChart(props) {
    var title = props.title, labels = props.labels, height = props.height, setBarData = props.setBarData;
    var stacked = isPropDefined(props.stacked);
    var originDatasets = initialiseDatasets(props.datasets);
    var _a = useState(originDatasets), datasets = _a[0], setDatasets = _a[1];
    // for keeping track of the legends click and order
    var _b = useState(null), prevOrder = _b[0], setPrevOrder = _b[1];
    var _c = useState(null), prevLegendItemIndex = _c[0], setPrevLegendItemIndex = _c[1];
    // Used to change the height of the y-axis when selecting a legend
    var _d = useState(null), maxHeight = _d[0], setMaxHeight = _d[1];
    // colours
    var titleColour = getCssVarValue("--bs-emphasis-color");
    var labelsAndGridColour = getCssVarValue("--bs-body-color");
    var gridLineColour = getCssVarValue("--bs-secondary-bg");
    // functions for options
    function handleLegendClick(event, legendItem, legend) {
        if (isPropDefined(setBarData)) {
            var legendIndex_1 = event.chart.data.datasets.findIndex(function (obj) { return obj.label === legendItem.text; });
            var selectedBucket_1 = null;
            // cannot keep clicking on the same legend item
            if (prevLegendItemIndex !== legendIndex_1) {
                legend.chart.data.datasets.forEach(function (dataset, index) {
                    if (index === legendIndex_1) {
                        dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, '1');
                        setPrevOrder(dataset.order);
                        setPrevLegendItemIndex(index);
                        dataset.order = -1;
                        selectedBucket_1 = dataset.id;
                        var maxValue = Math.max.apply(Math, dataset.data);
                        var maxValuePercentage = Math.round(maxValue + (maxValue * 0.1));
                        setMaxHeight(maxValuePercentage);
                    }
                    else {
                        dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, '0.25');
                        // reset prev item's order
                        if (prevLegendItemIndex === index) {
                            dataset.order = prevOrder;
                        }
                    }
                });
                // sets the bar data to the selected legend
                setBarData({
                    "bucket": selectedBucket_1,
                    "value": null,
                    "clickKey": null
                });
            }
            else {
                setMaxHeight(null);
                legend.chart.data.datasets.forEach(function (dataset, index) {
                    dataset.backgroundColor = updateOpacitys(dataset.backgroundColor, '1');
                    setPrevOrder(null);
                    setPrevLegendItemIndex(null);
                    dataset.order = index;
                });
                // sets the bar data to the selected legend
                setBarData({
                    "bucket": null,
                    "value": null,
                    "clickKey": null
                });
            }
            legend.chart.update();
            setDatasets(legend.chart.data.datasets);
        }
    }
    function handleLegendHover(event) {
        if (isPropDefined(setBarData)) {
            event.native.target.style.cursor = 'pointer';
        }
    }
    // @ts-ignore
    function handlePlaneClick(event, chartElement, chart, item) {
        setMaxHeight(null);
        if (item !== undefined) {
            return;
        }
        // reset order on 'plane reset click'
        if (prevLegendItemIndex !== null) {
            chart.data.datasets[prevLegendItemIndex].order = prevOrder;
            setPrevOrder(null);
            setPrevLegendItemIndex(null);
        }
        // only clickable if setBarData is defined
        if (isPropDefined(setBarData)) {
            if (!chartElement.length) {
                // reset bar colours when clicking any other part of chart
                updateChartColours(chart, true, 0.5);
                resetItemClickedData(setBarData);
            }
            else {
                // fade non-clicked bars
                updateChartColours(chart, false, 0.25);
                // setting clicked bar as its original colour
                setClickedColourToSolid(chart, chartElement);
                setBarClickedData(chart, chartElement, setBarData);
            }
            chart.update();
        }
        setDatasets(chart.data.datasets);
    }
    function handlePlaneHover(event, chartElement) {
        if (isPropDefined(setBarData)) {
            event.native.target.style.cursor = chartElement[0] ? "pointer" : "default";
        }
    }
    // chart options
    var options = {
        maintainAspectRatio: false,
        responsive: true,
        plugins: {
            title: {
                display: title !== undefined,
                text: title,
                color: titleColour
            },
            tooltip: {
                usePointStyle: true,
                backgroundColor: "black",
                callbacks: {
                    labelPointStyle: function () {
                        return {
                            pointStyle: 'rectRounded',
                            rotation: 0
                        };
                    },
                    labelColor: function (context) {
                        var colour = getChartColour(context.datasetIndex);
                        return {
                            backgroundColor: colour,
                            borderColor: colour
                        };
                    },
                }
            },
            legend: {
                onHover: handleLegendHover,
                onClick: handleLegendClick,
                labels: {
                    padding: 15,
                    usePointStyle: true,
                    generateLabels: function (chart) {
                        return generateBarLabels(chart, titleColour);
                    }
                }
            }
        },
        layout: {
            padding: {
                left: 10,
                right: 10,
                bottom: 10
            }
        },
        onClick: handlePlaneClick,
        onHover: handlePlaneHover,
        scales: {
            x: {
                stacked: stacked,
                grid: {
                    display: false
                },
                ticks: {
                    color: labelsAndGridColour
                }
            },
            y: {
                stacked: stacked,
                max: maxHeight,
                grid: {
                    color: gridLineColour
                },
                ticks: {
                    color: labelsAndGridColour
                }
            },
        }
    };
    return (_jsxs("div", __assign({ style: { height: height.toString() + 'px' } }, { children: [_jsx("div", __assign({ className: "tol-chart-buttons" }, { children: isPropDefined(setBarData) &&
                    _jsx(Button, __assign({ className: "config-button", variant: "primary", onClick: function () {
                            resetItemClickedData(setBarData);
                            setMaxHeight(null);
                            setDatasets(originDatasets);
                        } }, { children: _jsx(FontAwesomeIcon, { icon: faUndo, size: "sm" }) })) })), _jsx(Bar, { responsive: "true", id: "tol-bar-chart", className: "tol-bar-chart", datasetIdKey: "id", 
                // @ts-ignore
                options: options, data: {
                    labels: labels,
                    datasets: datasets
                } })] })));
}
export default BarChart;
